import { useState, useCallback, useMemo } from 'react';

export type TableDensity = 'compact' | 'detailed';
export type TableViewMode = 'list' | 'grid';

export interface TableSettings {
    density: TableDensity;
    columnDividers: boolean;
    viewMode?: TableViewMode;
}

const DEFAULT_SETTINGS: TableSettings = {
    density: 'detailed',
    columnDividers: false,
};

const PAGE_DEFAULTS: Record<string, Partial<TableSettings>> = {
    inventory: { viewMode: 'grid' },
};

const getStorageKey = (pageKey: string) => `table_settings_${pageKey}`;

const loadSettings = (pageKey: string): TableSettings => {
    const pageDefaults = { ...DEFAULT_SETTINGS, ...(PAGE_DEFAULTS[pageKey] ?? {}) };
    try {
        const saved = localStorage.getItem(getStorageKey(pageKey));
        if (saved) {
            const parsed = JSON.parse(saved);
            return { ...pageDefaults, ...parsed };
        }
    } catch {
        // ignore
    }
    return pageDefaults;
};

const saveSettings = (pageKey: string, settings: TableSettings) => {
    try {
        localStorage.setItem(getStorageKey(pageKey), JSON.stringify(settings));
    } catch {
        // ignore
    }
};

/**
 * Generates CSS-in-JS sx styles for the table container based on settings.
 * Apply these to the Box wrapping the table.
 */
export const getTableSettingsSx = (settings: TableSettings): Record<string, any> => {
    const styles: Record<string, any> = {};

    if (settings.density === 'compact') {
        // Reduce row height
        styles['& td'] = {
            ...(styles['& td'] || {}),
            py: '4px !important',
        };
        styles['& th'] = {
            ...(styles['& th'] || {}),
            py: '4px !important',
        };
        // Reduce font size for all text inside cells
        styles['& td .chakra-text'] = {
            fontSize: 'xs !important',
        };
        // Shrink VIN text further in compact mode
        styles['& [data-table-vin] .chakra-text'] = {
            fontSize: '10px !important',
        };
    }

    if (settings.columnDividers) {
        styles['& td'] = {
            ...(styles['& td'] || {}),
            borderRightWidth: '1px !important',
            borderRightStyle: 'solid !important',
            borderRightColor: 'var(--chakra-colors-gray-100) !important',
        };
        styles['& th'] = {
            ...(styles['& th'] || {}),
            borderRightWidth: '1px !important',
            borderRightStyle: 'solid !important',
            borderRightColor: 'var(--chakra-colors-gray-200) !important',
        };
        // Remove right border on last column
        styles['& td:last-child'] = {
            ...(styles['& td:last-child'] || {}),
            borderRightWidth: '0 !important',
        };
        styles['& th:last-child'] = {
            ...(styles['& th:last-child'] || {}),
            borderRightWidth: '0 !important',
        };
    }

    return styles;
};

export const useTableSettings = (pageKey: string) => {
    const [settings, setSettings] = useState<TableSettings>(() => loadSettings(pageKey));

    const updateSettings = useCallback((newSettings: Partial<TableSettings>) => {
        setSettings(prev => {
            const updated = { ...prev, ...newSettings };
            saveSettings(pageKey, updated);
            return updated;
        });
    }, [pageKey]);

    const tableSettingsSx = useMemo(() => getTableSettingsSx(settings), [settings]);

    return { settings, updateSettings, tableSettingsSx };
};
